import BaseDropDownList, {
    BaseDropDownListProps,
} from 'base-components/BaseDropDownList/BaseDropDownList';
import { DropDown } from 'models/api/dropDown';
import { useEffect } from 'react';
import {
    Controller,
    FieldPath,
    FieldValues,
    RegisterOptions,
    useFormContext,
} from 'react-hook-form';

export type BaseValidatedDropDownListProps<T extends FieldValues> = Omit<
    BaseDropDownListProps,
    'setValue' | 'name' | 'value' | 'disabled' | 'invalid' | 'required'
> & {
    onValueChange?: (value: DropDown) => void;
    rules?: RegisterOptions;
    name: FieldPath<T>;
    disabled?: ((formState: FieldValues) => boolean) | boolean;
    defaultItems?: DropDown[];
};

const BaseValidatedDropDownList = <T extends FieldValues>({
    name,
    rules,
    disabled,
    items,
    label,
    isLoading,
    onValueChange,
    defaultItems,
    ...props
}: BaseValidatedDropDownListProps<T>) => {
    const { register, unregister, control, getValues } = useFormContext<T>();

    useEffect(() => {
        register(name);
        return unregister(name);
    }, []);

    return (
        <Controller<T>
            name={name}
            control={control}
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                return (
                    <BaseDropDownList
                        name={name}
                        items={defaultItems ? defaultItems.concat(items) : items}
                        label={label}
                        value={value}
                        setValue={(v) => {
                            onChange(v.id ?? null);
                            onValueChange && onValueChange(v);
                        }}
                        isLoading={isLoading}
                        disabled={typeof disabled === 'function' ? disabled(getValues()) : disabled}
                        invalid={!!error?.message}
                        error={error?.message}
                        refCallback={ref}
                        {...props}
                    />
                );
            }}
            rules={rules as any}
        />
    );
};

export default BaseValidatedDropDownList;
